import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@client';
import Option from '../../../../../../types/option';
import { FleetResponseDto } from './teams/models';
import { EvaluationResponse } from '@module/evaluation/model/EvaluationResponse';
import { PortCallResponse } from '@state/types';
import { DaSummary } from '@module/reopen-da/model/DaSummary';
import { AccountingCodeResponse } from '@state/types/AccountingCodeResponse';
import { ExpenseEvaluationAdminSupportResponse } from '@state/types/ExpenseEvaluationAdminSupportResponse';
import { SupplierResponse } from '@module/supplier/model/SupplierResponse';
import { RequestForQuotationResponse } from '@module/request/model/RequestForQuotationResponse';
import { RefData } from 'components';
import { BuyerPreferredAgentsFormValues } from '../../../../../sitemap/secured/admin/support/tasks/AdminSupportTaskAssignBulkPreferredAgents';

export interface AmendPortCheckDto {
  newPortId: number;
  hasMultipleAppointments: boolean;
}

export interface EvaluationDataDTO {
  id: number;
  proformaId: number;
  proformaType: string;
  evaluationState: string;
  assigneeFullName: string;
  vesselName: string;
  portName: string;
}

export const adminSupportBuyerApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'adminSupportBuyer_api',
  endpoints: (build) => ({
    fetchBuyerFleets: build.query<FleetResponseDto[], { buyerId: number }>({
      query: ({ buyerId }) => ({
        url: `/secured/buyers/${buyerId}/fleets`,
        method: 'GET'
      })
    }),
    fetchBuyerRuleOptions: build.query<Option[], { buyerId: number }>({
      query: ({ buyerId }) => ({
        url: `/secured/rules`,
        method: 'GET',
        params: { buyerId, size: 1000, page: 0 }
      }),
      transformResponse: (response: {
        items: { id: number; name: string }[];
      }): Option[] => {
        const items = response.items ?? [];
        return items.map((item) => ({ label: item.name, value: item.id }));
      }
    }),
    fetchEvaluationByFormId: build.query<EvaluationResponse, number>({
      query: (formId) => ({
        url: `/secured/form/${formId}/evaluation`,
        method: 'GET'
      })
    }),
    amendInvoiceNumber: build.mutation<
      EvaluationResponse,
      { formId: number; invoiceNumber: string }
    >({
      query: ({ formId, invoiceNumber }) => ({
        url: `/secured/form/${formId}/amend-invoice-number`,
        method: 'POST',
        data: invoiceNumber
      })
    }),
    fetchPortCallById: build.query<PortCallResponse, number>({
      query: (portCallId) => ({
        url: `/secured/portcall/${portCallId}/load`,
        method: 'GET'
      })
    }),
    updatePortCallState: build.mutation<
      PortCallResponse,
      { portCallId: number; newState: string }
    >({
      query: ({ portCallId, newState }) => ({
        url: `/secured/portcall/${portCallId}/update-status`,
        method: 'PATCH',
        params: { status: newState }
      })
    }),
    fetchDaSummary: build.query<DaSummary, { proformaId: string }>({
      query: ({ proformaId }) => ({
        url: `/secured/disbursements/${proformaId}`,
        method: 'GET'
      })
    }),
    unlinkPortCallId: build.mutation<PortCallResponse, { portCallId: number }>({
      query: ({ portCallId }) => ({
        url: `/secured/portcall/${portCallId}/unlink-port-call`,
        method: 'POST'
      })
    }),
    getExpenseEvaluationByFormIdAndName: build.query<
      ExpenseEvaluationAdminSupportResponse,
      { formId: number; expenseName: string }
    >({
      query: ({ formId, expenseName }) => ({
        url: `/secured/form/${formId}/expense-evaluation/load`,
        method: 'GET',
        params: { expenseName }
      })
    }),
    getBuyerAccountingCodes: build.query<AccountingCodeResponse[], number>({
      query: (buyerId) => ({
        url: `/secured/buyer/${buyerId}/accounting-codes`,
        method: 'GET'
      })
    }),
    assignAccountingCodeToExpenseEvaluation: build.mutation<
      void,
      { expenseEvaluationId: number; accountingCodeId: number }
    >({
      query: ({ expenseEvaluationId, accountingCodeId }) => ({
        url: `/secured/expense-evaluation/${expenseEvaluationId}/assign-accounting-code`,
        method: 'POST',
        params: { accountingCodeId }
      })
    }),
    fetchAgentRefDataById: build.query<SupplierResponse, { agentId: number }>({
      query: ({ agentId }) => ({
        url: `/secured/agent?id=${agentId}`,
        method: 'GET'
      })
    }),
    assignPreferredAgentsToBuyer: build.mutation<
      PortCallResponse,
      {
        buyerId: number;
        agentData: BuyerPreferredAgentsFormValues;
      }
    >({
      query: ({ buyerId, agentData }) => ({
        url: `/secured/buyer/${buyerId}/assign-preferred-agents`,
        method: 'POST',
        data: agentData
      })
    }),
    uploadPreferredAgentsTemplate: build.mutation<
      void,
      {
        data: FormData;
      }
    >({
      query: ({ data }) => ({
        url: `/secured/attachment/buyer/assign-preferred-agents`,
        method: 'POST',
        data: data,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        formData: true
      })
    }),
    fetchRequestForQuotation: build.query<
      RequestForQuotationResponse,
      { requestCaseId: number; agentId: number }
    >({
      query: ({ requestCaseId, agentId }) => ({
        url: `/secured/agent/${agentId}/rfq/${requestCaseId}/load`,
        method: 'GET'
      })
    }),
    revertRequestForQuotation: build.mutation<
      void,
      { requestCaseId: number; agentId: number }
    >({
      query: ({ requestCaseId, agentId }) => ({
        url: `/secured/agent/${agentId}/rfq/${requestCaseId}/revert`,
        method: 'POST'
      })
    }),
    getPortCallByAppointment: build.query<PortCallResponse, number>({
      query: (appointmentId) => ({
        url: `/secured/appointment/${appointmentId}/port-call/load`,
        method: 'GET'
      })
    }),
    checkAmendPort: build.query<
      AmendPortCheckDto,
      { portCallId: number; newPortId: number }
    >({
      query: ({ portCallId, newPortId }) => ({
        url: `/secured/port-call/${portCallId}/amend-port/check`,
        method: 'GET',
        params: { newPortId }
      })
    }),
    amendPort: build.mutation<
      PortCallResponse,
      { portCallId: number; dto: AmendPortCheckDto }
    >({
      query: ({ portCallId, dto }) => ({
        url: `/secured/port-call/${portCallId}/amend-port`,
        method: 'POST',
        data: dto
      })
    }),
    deleteFinancialPayment: build.mutation<void, { proformaId: number }>({
      query: ({ proformaId }) => ({
        url: `/secured/proforma/${proformaId}/payment/delete`,
        method: 'POST'
      })
    }),
    linkPortCallToAppointment: build.mutation<
      void,
      { appointmentId: number; portCallId: number }
    >({
      query: ({ appointmentId, portCallId }) => ({
        url: `/secured/appointment/${appointmentId}/port-call/${portCallId}/link`,
        method: 'POST'
      })
    }),
    fetchBuyerPreferredAgents: build.query<RefData[], { buyerId: number }>({
      query: ({ buyerId }) => ({
        url: `/secured/buyer/${buyerId}/preferred-agents`,
        method: 'GET'
      })
    }),
    fetchEvaluationDataByProformaId: build.query<
      EvaluationDataDTO,
      { proformaId: number }
    >({
      query: ({ proformaId }) => ({
        url: `/secured/proforma/${proformaId}/evaluation-data`,
        method: 'GET'
      })
    }),
    fetchEligibleBuyerProfiles: build.query<RefData[], { proformaId: number }>({
      query: ({ proformaId }) => ({
        url: `/secured/proforma/${proformaId}/eligible-assignees`,
        method: 'GET'
      })
    }),
    removeDaAssignee: build.mutation<RefData[], { proformaId: number }>({
      query: ({ proformaId }) => ({
        url: `/secured/proforma/${proformaId}/remove-assignee`,
        method: 'POST'
      })
    }),
    addDaAssignee: build.mutation<
      RefData[],
      { proformaId: number; profileId: number }
    >({
      query: ({ proformaId, profileId }) => ({
        url: `/secured/proforma/${proformaId}/profile/${profileId}/assign`,
        method: 'POST'
      })
    })
  })
});

export type StateWithAdminSupportBuyerApi = {
  [adminSupportBuyerApi.reducerPath]: ReturnType<
    typeof adminSupportBuyerApi.reducer
  >;
};

export const {
  useFetchBuyerRuleOptionsQuery,
  useFetchBuyerFleetsQuery,
  useLazyFetchPortCallByIdQuery,
  useUpdatePortCallStateMutation,
  useAmendInvoiceNumberMutation,
  useLazyFetchEvaluationByFormIdQuery,
  useFetchDaSummaryQuery,
  useLazyFetchDaSummaryQuery,
  useUnlinkPortCallIdMutation,
  useLazyGetExpenseEvaluationByFormIdAndNameQuery,
  useGetBuyerAccountingCodesQuery,
  useLazyGetBuyerAccountingCodesQuery,
  useAssignAccountingCodeToExpenseEvaluationMutation,
  useLazyFetchAgentRefDataByIdQuery,
  useAssignPreferredAgentsToBuyerMutation,
  useUploadPreferredAgentsTemplateMutation,
  useLazyFetchRequestForQuotationQuery,
  useRevertRequestForQuotationMutation,
  useLazyGetPortCallByAppointmentQuery,
  useLazyCheckAmendPortQuery,
  useAmendPortMutation,
  useDeleteFinancialPaymentMutation,
  useLinkPortCallToAppointmentMutation,
  useFetchBuyerPreferredAgentsQuery,
  useLazyFetchEvaluationDataByProformaIdQuery,
  useLazyFetchEligibleBuyerProfilesQuery,
  useRemoveDaAssigneeMutation,
  useAddDaAssigneeMutation
} = adminSupportBuyerApi;
